import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ClipPublic } from '@proref/types';
import { useAuth } from '../../contexts/auth-context';
import { useSportsQuery } from '../../hooks/use-sports';
import { useFederationsQuery } from '../../hooks/use-federations';
import { useAdminClipsQuery } from '../../hooks/use-clips';
import { useDailyQueueQuery, useReorderDailyQueueMutation } from '../../hooks/use-daily-queue';
import { PageHeader } from '../../components/shared/PageHeader';
import { Card } from '../../components/shared/Card';
import { ListSkeleton } from '../../components/shared/Skeleton';
import { Icon } from '../../components/shared/Icons';
import { notifySuccess, notifyError } from '../../lib/toast';

const FORECAST_DAYS = 14;

const SELECT_CLASS = [
  'h-10 rounded-sm border border-line-2 bg-surface px-2.5 pr-7 text-sm text-ink',
  'outline-none transition-[border-color,box-shadow]',
  'focus:border-ink-3 focus:shadow-[0_0_0_3px_color-mix(in_oklch,var(--ink)_10%,transparent)]',
].join(' ');

// Absolute rotation order (by queue_position, upload-order tiebreak) — the SAME ordering
// select_daily_clip reads. Distinct from the upcoming-forecast list below, which is rotated to
// start at today and can repeat clips across a multi-week window; this one is each published clip
// exactly once, in the order a reorder actually writes.
function rotationOrder(clips: ClipPublic[]): ClipPublic[] {
  return clips
    .filter((c) => c.published_at !== null)
    .slice()
    .sort((a, b) => {
      const pa = a.queue_position ?? Number.MAX_SAFE_INTEGER;
      const pb = b.queue_position ?? Number.MAX_SAFE_INTEGER;
      if (pa !== pb) return pa - pb;
      return a.uploaded_at.localeCompare(b.uploaded_at);
    });
}

// Federation+sport view of the daily-clip rotation: an upcoming-days forecast (what a referee
// will actually be assigned, per day) plus the reorderable master list that forecast is built
// from. super_admin picks a sport + federation first; a federation_admin's own scope is implicit.
export function DailyQueuePage() {
  const { t } = useTranslation();
  const { role, user } = useAuth();
  const isSuperAdmin = role === 'super_admin';

  const sportsQuery = useSportsQuery(isSuperAdmin);
  const federationsQuery = useFederationsQuery(isSuperAdmin);

  const [pickedSportId, setPickedSportId] = useState('');
  const [pickedFederationId, setPickedFederationId] = useState('');

  const sportId = isSuperAdmin ? pickedSportId : (user?.sport_id ?? '');
  const federationId = isSuperAdmin ? pickedFederationId : undefined;
  const ready = Boolean(sportId) && (!isSuperAdmin || Boolean(federationId));

  const queueParams = { sportId, federationId, days: FORECAST_DAYS };
  const queueQuery = useDailyQueueQuery(queueParams, ready);
  const clipsQuery = useAdminClipsQuery({ sportId: ready ? sportId : undefined, federationId });
  const reorder = useReorderDailyQueueMutation(queueParams);

  const order = useMemo(() => rotationOrder(clipsQuery.data ?? []), [clipsQuery.data]);

  function move(index: number, delta: number) {
    const target = index + delta;
    if (target < 0 || target >= order.length) return;
    const next = order.slice();
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved!);
    reorder.mutate(
      next.map((c) => c.id),
      {
        onSuccess: () => notifySuccess(t('admin.dailyQueue.reorderedToast')),
        onError: (err) => notifyError(err, t('admin.dailyQueue.reorderErrorToast')),
      },
    );
  }

  return (
    <>
      <PageHeader title={t('admin.dailyQueue.title')} sub={t('admin.dailyQueue.sub')} />

      {isSuperAdmin && (
        <div className="mb-5 flex flex-wrap gap-3">
          <select
            aria-label={t('admin.dailyQueue.selectSport')}
            className={SELECT_CLASS}
            value={pickedSportId}
            onChange={(e) => setPickedSportId(e.target.value)}
          >
            <option value="">{t('admin.dailyQueue.selectSport')}</option>
            {(sportsQuery.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          <select
            aria-label={t('admin.dailyQueue.selectFederation')}
            className={SELECT_CLASS}
            value={pickedFederationId}
            onChange={(e) => setPickedFederationId(e.target.value)}
          >
            <option value="">{t('admin.dailyQueue.selectFederation')}</option>
            {(federationsQuery.data ?? []).map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {!ready ? (
        <p className="text-sm text-muted">{t('admin.dailyQueue.pickPrompt')}</p>
      ) : (
        <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-2">
          <Card title={t('admin.dailyQueue.upcomingTitle')} sub={t('admin.dailyQueue.upcomingSub')}>
            {queueQuery.isLoading ? (
              <ListSkeleton rows={8} />
            ) : queueQuery.isError ? (
              <p className="text-sm text-danger">{t('admin.dailyQueue.couldNotLoad')}</p>
            ) : (
              <ul className="divide-y divide-line">
                {(queueQuery.data ?? []).map((entry) => (
                  <li key={entry.scheduled_on} className="flex items-center gap-3 py-2.5">
                    <div className="w-[88px] shrink-0 text-xs text-muted">{entry.scheduled_on}</div>
                    {entry.clip ? (
                      <>
                        {entry.clip.poster_url && (
                          <img
                            src={entry.clip.poster_url}
                            alt=""
                            className="h-9 w-16 shrink-0 rounded object-cover"
                          />
                        )}
                        <div className="min-w-0 flex-1 truncate text-sm text-ink">{entry.clip.title}</div>
                      </>
                    ) : (
                      <div className="flex-1 text-sm text-muted">{t('admin.dailyQueue.emptyDay')}</div>
                    )}
                    {entry.is_today && (
                      <span className="shrink-0 rounded-full bg-accent-tint px-2 py-0.5 text-[11px] font-medium text-accent">
                        {t('admin.dailyQueue.todayBadge')}
                      </span>
                    )}
                    {entry.is_pinned && (
                      <span className="shrink-0 rounded-full bg-warn-tint px-2 py-0.5 text-[11px] font-medium text-warn">
                        {t('admin.dailyQueue.pinnedBadge')}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card title={t('admin.dailyQueue.rotationTitle')} sub={t('admin.dailyQueue.rotationSub')}>
            {clipsQuery.isLoading ? (
              <ListSkeleton rows={6} />
            ) : order.length === 0 ? (
              <p className="text-sm text-muted">{t('admin.dailyQueue.noClipsYet')}</p>
            ) : (
              <ol className="divide-y divide-line">
                {order.map((clip, index) => (
                  <li key={clip.id} className="flex items-center gap-3 py-2.5">
                    <div className="w-6 shrink-0 text-xs text-muted">{index + 1}</div>
                    <div className="min-w-0 flex-1 truncate text-sm text-ink">{clip.title}</div>
                    <div className="flex shrink-0 gap-1">
                      <button
                        type="button"
                        aria-label={t('admin.dailyQueue.moveUpAria', { title: clip.title })}
                        disabled={index === 0 || reorder.isPending}
                        onClick={() => move(index, -1)}
                        className="rounded p-1.5 text-ink-2 hover:bg-surface-2 hover:text-ink disabled:opacity-30"
                      >
                        <Icon.arrowDown className="h-4 w-4 rotate-180" />
                      </button>
                      <button
                        type="button"
                        aria-label={t('admin.dailyQueue.moveDownAria', { title: clip.title })}
                        disabled={index === order.length - 1 || reorder.isPending}
                        onClick={() => move(index, 1)}
                        className="rounded p-1.5 text-ink-2 hover:bg-surface-2 hover:text-ink disabled:opacity-30"
                      >
                        <Icon.arrowDown className="h-4 w-4" />
                      </button>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </Card>
        </div>
      )}
    </>
  );
}
